/**
 * PublicSidebar - 门户侧边栏组件
 * 
 * Apple 风格设计，支持明暗模式
 * 功能：文件夹树、订单查询、标签筛选
 * 
 * 折叠状态优化：使用统一图标 + Tooltip 显示完整名称
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Folder, FolderOpen, Tag as TagIcon, 
  Home, ChevronDown, ChevronRight,
  Eye, EyeOff, Shield, X, Filter, Star,
  ChevronLeft, Package
} from 'lucide-react';
import { supabase } from '../../config/supabase';
import { Tag, Category } from '../../types';
import { cn } from '../../utils/cn';
import { useI18n } from '../../i18n';
import { useTenant } from '../../contexts/TenantContext';

// Tooltip 组件 - 用于折叠状态显示完整名称
// 使用 fixed 定位避免被容器 overflow 裁剪
interface TooltipProps {
  children: React.ReactNode;
  content: string;
  show: boolean;
}

const SidebarTooltip: React.FC<TooltipProps> = ({ children, content, show }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  
  const handleMouseEnter = () => {
    if (!show) return;
    setIsHovered(true);
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 12
      });
    }
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  
  return (
    <div 
      ref={triggerRef}
      className="portal-sidebar-tooltip-trigger"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {show && isHovered && (
        <div 
          className="portal-sidebar-tooltip-fixed"
          style={{ top: position.top, left: position.left }}
        >
          <span>{content}</span>
          <div className="portal-sidebar-tooltip-arrow" />
        </div>
      )}
    </div>
  );
};

interface PublicSidebarProps {
  collapsed: boolean;
  mobileOpen?: boolean;
  onToggle?: () => void;
  onMobileToggle?: () => void;
  selectedTags?: string[];
  selectedCategory?: string | null;
  onTagToggle?: (tagId: string) => void;
  onCategorySelect?: (categoryId: string | null) => void;
  onResetFilters?: () => void;
  onOrderQuery?: (orderNumber: string) => void;
  refreshTrigger?: number;
  user?: any;
  isAuthenticated?: boolean;
  availableTags?: Tag[];
}

const PublicSidebar: React.FC<PublicSidebarProps> = ({ 
  collapsed, 
  mobileOpen = false,
  onToggle,
  onMobileToggle,
  selectedTags = [],
  selectedCategory = null,
  onTagToggle = () => {},
  onCategorySelect = () => {},
  onResetFilters = () => {},
  onOrderQuery = () => {},
  refreshTrigger,
  user,
  isAuthenticated = false,
  availableTags: externalTags
}) => {
  const { t } = useI18n();
  const { tenant } = useTenant(); // 获取当前租户 (cn/intl)
  const [internalTags, setInternalTags] = useState<Tag[]>([]);
  
  // 最终使用的标签列表：优先使用外部传入的（基于当前文件自动生成的），否则回退到内部获取的（全量可见标签）
  const availableTags = externalTags || internalTags;
  const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  
  // 订单查询状态
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [isSearchingOrder, setIsSearchingOrder] = useState<boolean>(false);
  const [showOrderQuery, setShowOrderQuery] = useState<boolean>(false);
  
  // 收藏功能
  const [favoriteFolders, setFavoriteFolders] = useState<string[]>([]);
  const [showTagFilter, setShowTagFilter] = useState<boolean>(true);

  useEffect(() => {
    fetchTagsAndCategories();
    loadFavorites();
  }, [isAuthenticated, user, tenant]); // 添加 tenant 依赖

  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchTagsAndCategories();
    }
  }, [refreshTrigger]);

  const loadFavorites = () => {
    try {
      const saved = localStorage.getItem('favoriteFolders');
      if (saved) {
        setFavoriteFolders(JSON.parse(saved));
      }
    } catch (error) {
      console.error('加载收藏失败:', error);
    }
  };

  const toggleFavorite = (categoryId: string) => {
    const newFavorites = favoriteFolders.includes(categoryId)
      ? favoriteFolders.filter(id => id !== categoryId)
      : [...favoriteFolders, categoryId];
    
    setFavoriteFolders(newFavorites);
    localStorage.setItem('favoriteFolders', JSON.stringify(newFavorites));
  };

  const getVisibilityInfo = (category: Category) => {
    const visibilityType = category.visibility_type || (category.is_public ? 'public' : 'private');
    
    switch (visibilityType) {
      case 'public':
        return { label: t('portal.visibilityPublic'), icon: Eye, className: 'text-emerald-600' };
      case 'restricted_public':
        return { label: t('portal.visibilityRestricted'), icon: Shield, className: 'text-amber-600' };
      default:
        return { label: t('portal.visibilityPrivate'), icon: EyeOff, className: 'text-gray-400' };
    }
  };

  const fetchTagsAndCategories = async () => {
    try {
      let cats: Category[] = [];
      
      if (isAuthenticated && user) {
        // 获取当前租户的公共文件夹
        const { data: publicCats, error: publicError } = await supabase
          .from('categories')
          .select('id, name, parent_id, created_at, is_public, visibility_type, file_count:file_categories(count)')
          .eq('tenant', tenant) // 按租户过滤
          .or('is_public.eq.true,visibility_type.eq.public')
          .neq('visibility_type', 'restricted_public')
          .order('name');

        if (publicError) throw publicError;

        // 获取当前租户中用户有权限的私有文件夹
        const { data: privateCats, error: privateError } = await supabase
          .from('categories')
          .select(`
            id, name, parent_id, created_at, is_public, visibility_type,
            file_count:file_categories(count),
            category_permissions!inner(user_id)
          `)
          .eq('tenant', tenant) // 按租户过滤
          .or('is_public.eq.false,visibility_type.eq.private,visibility_type.eq.restricted_public')
          .eq('category_permissions.user_id', user.id)
          .order('name');

        if (privateError) throw privateError;

        const allCats = [...(publicCats || []), ...(privateCats || [])];
        const uniqueCats = allCats.filter((cat, index, self) => 
          index === self.findIndex(c => c.id === cat.id)
        );
        
        cats = uniqueCats as Category[];
      } else {
        // 未登录用户：仅获取当前租户的公共文件夹
        const { data: publicCats, error: catsError } = await supabase
          .from('categories')
          .select('id, name, parent_id, created_at, is_public, visibility_type, file_count:file_categories(count)')
          .eq('tenant', tenant) // 按租户过滤
          .or('is_public.eq.true,visibility_type.eq.public')
          .neq('visibility_type', 'restricted_public')
          .order('name');

        if (catsError) throw catsError;
        cats = (publicCats || []) as Category[];
      }

      const normalizeCategory = (cat: any) => ({
        ...cat,
        file_count: cat.file_count?.[0]?.count || 0
      });

      const processedCats = (cats || []).map(normalizeCategory);
      setAvailableCategories([]); // Clear first to avoid tree build issues if needed, or just set it later
      
      const buildCategoryTree = (categories: any[], parentId: string | null = null): Category[] => {
        return categories
          .filter(cat => cat.parent_id === parentId)
          .map(cat => ({
            ...cat,
            children: buildCategoryTree(categories, cat.id)
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
      };

      const categoryTree = buildCategoryTree(processedCats);
      setAvailableCategories(categoryTree);
      setExpandedCategories(categoryTree.map(c => c.id));

      // 优化标签获取：直接从所有的标签中筛选当前租户可见的
      // 避免使用庞大的 fileId 列表
      const { data: tagsData, error: tagsError } = await supabase
        .from('file_tags')
        .select(`
          tags!inner(id, name, color),
          files!inner(id, tenant, is_public, owner_only, deleted_at)
        `)
        .eq('files.tenant', tenant)
        .eq('files.is_public', true)
        .eq('files.owner_only', false)
        .is('files.deleted_at', null)
        .limit(100); // 限制返回数量避免过载

      if (tagsError) {
        console.error('Fetch tags improved error:', tagsError);
        setInternalTags([]);
      } else {
        const uniqueTags = Array.from(
          new Map((tagsData || []).map((item: any) => [item.tags.id, item.tags])).values()
        ) as Tag[];
        setInternalTags(uniqueTags);
      }
    } catch (error) {
      console.error('获取标签和分类失败:', error);
    }
  };

  // ✅ 已删除 fetchCategoryCounts - 不再执行高消耗的全量文件计数
  // 逻辑已合并到 fetchTagsAndCategories 的联表查询中
  const fetchCategoryCounts = async (_categoryIds: string[]) => {
    // 功能已废弃，由 category.file_count 代替
  };

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleOrderQuery = async () => {
    if (!orderNumber.trim() || !user || isSearchingOrder) return;
    
    try {
      setIsSearchingOrder(true);
      await onOrderQuery(orderNumber.trim());
      setOrderNumber('');
      setShowOrderQuery(false);
    } catch (error) {
      console.error('订单查询失败:', error);
    } finally {
      setIsSearchingOrder(false);
    }
  };

  const handleOrderInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleOrderQuery();
    }
  };

  const filterCount = selectedTags.length + (selectedCategory ? 1 : 0);

  // 递归检查文件夹及其子文件夹是否包含文件
  const hasFilesInBranch = (cat: Category): boolean => {
    if ((cat.file_count || 0) > 0) return true;
    if (cat.children && cat.children.length > 0) {
      return cat.children.some(child => hasFilesInBranch(child));
    }
    return false;
  };

  const renderCategoryTree = (categories: Category[], level = 0) => {
    // 过滤掉完全没有文件的文件夹分支（除非是特殊的空文件夹搜索，但这里逻辑统一）
    const visibleCategories = categories.filter(cat => hasFilesInBranch(cat));

    return visibleCategories.map(category => {
      const isExpanded = expandedCategories.includes(category.id);
      const isSelected = selectedCategory === category.id;
      const hasChildren = category.children && category.children.length > 0;
      const visibleChildren = category.children?.filter(c => hasFilesInBranch(c)) || [];
      const hasVisibleChildren = visibleChildren.length > 0;
      
      const visibility = getVisibilityInfo(category);
      const VisibilityIcon = visibility.icon;

      return (
        <div key={category.id} className="portal-sidebar-tree-node">
          {collapsed ? (
            // 折叠状态：仅显示图标
            <SidebarTooltip content={category.name} show={true}>
              <button
                onClick={() => {
                  onCategorySelect(category.id);
                  if (onToggle) onToggle();
                }}
                className={cn(
                  'portal-sidebar-item-collapsed w-full',
                  isSelected && 'portal-sidebar-item-collapsed--active'
                )}
              >
                <div className="relative">
                  <Folder 
                    size={18} 
                    className={cn(
                      'portal-sidebar-collapsed-icon-simple',
                      isSelected ? 'text-[var(--accent-blue)]' : 'text-amber-500'
                    )} 
                  />
                  {category.file_count! > 0 && (
                    <span className="portal-sidebar-collapsed-badge-corner-small" />
                  )}
                </div>
              </button>
            </SidebarTooltip>
          ) : (
            // 展开状态：完整树结构
            <>
              <div 
                className={cn(
                  'portal-sidebar-folder group',
                  isSelected && 'portal-sidebar-folder--active'
                )}
                style={{ paddingLeft: `${level * 12 + 8}px` }}
              >
                <div className="flex items-center flex-1 min-w-0">
                  <button 
                    onClick={() => hasVisibleChildren && toggleCategoryExpansion(category.id)}
                    className={cn(
                      'p-1 mr-1 rounded-md hover:bg-[var(--bg-tertiary)] transition-colors',
                      !hasVisibleChildren && 'invisible'
                    )}
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  
                  <div 
                    className="flex items-center flex-1 min-w-0 cursor-pointer py-1"
                    onClick={() => onCategorySelect(category.id)}
                  >
                    {isSelected ? (
                      <FolderOpen size={16} className="mr-2 text-[var(--accent-blue)] shrink-0" />
                    ) : (
                      <Folder size={16} className="mr-2 text-amber-500 shrink-0" />
                    )}
                    <span className="portal-sidebar-folder-name truncate">{category.name}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity pr-2 shrink-0">
                  <SidebarTooltip content={visibility.label} show={true}>
                    <VisibilityIcon size={12} className={visibility.className} />
                  </SidebarTooltip>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(category.id);
                    }}
                    className="p-1 rounded-md hover:bg-[var(--bg-tertiary)] transition-colors"
                  >
                    <Star 
                      size={12} 
                      className={cn(
                        favoriteFolders.includes(category.id) 
                          ? 'text-amber-500 fill-amber-500' 
                          : 'text-[var(--text-tertiary)]'
                      )} 
                    />
                  </button>
                  {category.file_count! > 0 && (
                    <span className="text-[10px] bg-[var(--bg-tertiary)] text-[var(--text-secondary)] px-1.5 py-0.5 rounded-full font-medium">
                      {category.file_count}
                    </span>
                  )}
                </div>
              </div>
              
              {isExpanded && hasVisibleChildren && (
                <div className="portal-sidebar-tree-children">
                  {renderCategoryTree(category.children!, level + 1)}
                </div>
              )}
            </>
          )}
        </div>
      );
    });
  };

  return (
    <>
      {/* 移动端遮罩 */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden animate-fade-in"
          onClick={onMobileToggle}
        />
      )}

      <aside className={cn(
        'portal-sidebar group',
        collapsed ? 'portal-sidebar--collapsed' : 'portal-sidebar--expanded',
        mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      )}>
        {/* 顶部控制栏 (仅桌面端) - 包含 Logo 和折叠按钮 */}
        {!mobileOpen && (
          <div className={cn(
            "portal-sidebar-header flex items-center border-b border-[var(--border-primary)]",
            collapsed ? "justify-center px-2 py-4" : "justify-between px-4 py-4"
          )}>
            {/* Logo - 仅展开状态显示 */}
            {!collapsed && (
              <img 
                src="/logo_24.svg" 
                alt="Logo" 
                className="h-6 w-auto"
              />
            )}
            {/* 折叠/展开按钮 */}
            <button 
              onClick={onToggle}
              className="portal-sidebar-toggle-btn opacity-60 hover:opacity-100 transition-opacity"
              title={collapsed ? t('portal.expandSidebar') : t('portal.collapseSidebar')}
            >
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>
        )}

        <div className="portal-sidebar-content custom-scrollbar">
          {/* 顶层导航 */}
          <div className="portal-sidebar-section mb-4">
            <div className="space-y-1">
              {collapsed ? (
                <SidebarTooltip content={t('portal.allFiles')} show={true}>
                  <button 
                    onClick={() => onCategorySelect(null)}
                    className={cn(
                      'portal-sidebar-item-collapsed w-full',
                      !selectedCategory && 'portal-sidebar-item-collapsed--active'
                    )}
                  >
                    <Home size={18} className="portal-sidebar-collapsed-icon-simple" />
                  </button>
                </SidebarTooltip>
              ) : (
                <button
                  onClick={() => onCategorySelect(null)}
                  className={cn(
                    'portal-sidebar-nav-item w-full',
                    !selectedCategory && 'portal-sidebar-nav-item--active'
                  )}
                >
                  <Home size={16} className="mr-3" />
                  <span className="font-medium">{t('portal.allFiles')}</span>
                  {!selectedCategory && <div className="portal-sidebar-nav-indicator" />}
                </button>
              )}
            </div>
          </div>

          {/* 订单查询区域 */}
          {isAuthenticated && (
            <div className="portal-sidebar-section mb-6">
              {collapsed ? (
                <SidebarTooltip content={t('portal.orderQuery')} show={true}>
                  <button
                    onClick={() => {
                      if (onToggle) onToggle();
                      setShowOrderQuery(true);
                    }}
                    className="portal-sidebar-item-collapsed w-full"
                  >
                    <Package size={18} className="portal-sidebar-collapsed-icon-simple text-amber-500" />
                  </button>
                </SidebarTooltip>
              ) : (
                <>
                  <button
                    onClick={() => setShowOrderQuery(!showOrderQuery)}
                    className="portal-sidebar-order-btn w-full flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <div className="w-7 h-7 bg-amber-50 dark:bg-amber-500/10 rounded-lg flex items-center justify-center mr-3">
                        <Package size={14} className="text-amber-600 dark:text-amber-500" />
                      </div>
                      <span className="font-semibold text-sm">{t('portal.orderQuery')}</span>
                    </div>
                    <ChevronDown size={14} className={cn('transition-transform text-gray-400', showOrderQuery && 'rotate-180')} />
                  </button>

                  {showOrderQuery && (
                    <div className="mt-2 px-1 animate-in fade-in slide-in-from-top-1 duration-200">
                      <div className="relative">
                        <input
                          type="text"
                          value={orderNumber}
                          onChange={(e) => setOrderNumber(e.target.value)}
                          onKeyPress={handleOrderInputKeyPress}
                          placeholder={t('portal.orderNumberInputPlaceholder')}
                          className="portal-sidebar-order-input w-full pr-16"
                        />
                        <button
                          onClick={handleOrderQuery}
                          disabled={isSearchingOrder || !orderNumber.trim()}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 px-2.5 py-1 text-xs font-medium text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-md transition-colors"
                        >
                          {isSearchingOrder ? (
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            t('portal.queryButton')
                          )}
                        </button>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1.5 px-1 leading-relaxed italic">
                        {t('portal.orderQueryHint', '输入文件夹对应的订单号，获取限时访问权限')}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* 收藏夹 */}
          {!collapsed && favoriteFolders.length > 0 && (
            <div className="portal-sidebar-section mb-4">
              <h3 className="portal-sidebar-section-header">
                <span className="flex items-center">
                  <Star className="w-4 h-4 mr-2 text-amber-500 fill-amber-500" />
                  {t('portal.favorites')}
                </span>
              </h3>
              <div className="space-y-1">
                {availableCategories.filter(c => favoriteFolders.includes(c.id)).map(category => (
                  <button
                    key={category.id}
                    onClick={() => {
                      onCategorySelect(category.id);
                      if (collapsed && onToggle) onToggle();
                    }}
                    className={cn(
                      'portal-sidebar-folder w-full',
                      selectedCategory === category.id && 'portal-sidebar-folder--active'
                    )}
                    title={category.name}
                  >
                    <Folder size={14} className={cn(selectedCategory === category.id ? 'text-[var(--accent-blue)]' : 'text-amber-500')} />
                    <span className="portal-sidebar-folder-name">{category.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 文件夹区域 */}
          <div className="portal-sidebar-section">
            {!collapsed && (
              <h3 className="portal-sidebar-section-header">
                <span className="flex items-center">
                  <Folder className="w-4 h-4 mr-2 text-[var(--accent-blue)]" />
                  {t('portal.folders')} ({availableCategories.length})
                </span>
              </h3>
            )}
            
            {/* 折叠状态：显示分隔线 */}
            {collapsed && availableCategories.length > 0 && (
              <div className="portal-sidebar-collapsed-divider" />
            )}
            
            <div className={cn('space-y-1', collapsed && 'portal-sidebar-collapsed-list')}>
              {availableCategories.length > 0 ? (
                renderCategoryTree(availableCategories)
              ) : (
                !collapsed && (
                  <div className="text-center py-8 text-[var(--text-tertiary)]">
                    <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('portal.noFolders')}</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* 标签区域 */}
        {availableTags.length > 0 && (
          <div className={cn(
            'border-t border-[var(--border-primary)]',
            collapsed ? 'p-2' : 'p-4'
          )}>
            {collapsed ? (
              // 折叠状态：显示标签图标
              <SidebarTooltip content={`${t('tags')} (${availableTags.length})`} show={true}>
                <button
                  onClick={() => {
                    if (onToggle) onToggle();
                    setShowTagFilter(true);
                  }}
                  className={cn(
                    'portal-sidebar-item-collapsed w-full',
                    selectedTags.length > 0 && 'portal-sidebar-item-collapsed--active'
                  )}
                >
                  <TagIcon size={18} className="portal-sidebar-collapsed-icon-simple" />
                  {selectedTags.length > 0 && (
                    <span className="portal-sidebar-collapsed-badge-corner">{selectedTags.length}</span>
                  )}
                </button>
              </SidebarTooltip>
            ) : (
              // 展开状态：显示完整标签列表
              <>
                <button
                  onClick={() => setShowTagFilter(!showTagFilter)}
                  className="flex items-center justify-between w-full mb-3"
                >
                  <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center">
                    <TagIcon className="w-4 h-4 mr-2 text-[var(--accent-blue)]" />
                    {t('tags')} ({availableTags.length})
                  </h3>
                  <ChevronDown size={14} className={cn('transition-transform text-[var(--text-tertiary)]', showTagFilter && 'rotate-180')} />
                </button>
                
                {showTagFilter && (
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => onTagToggle(tag.id)}
                        className={cn(
                          'portal-sidebar-tag',
                          selectedTags.includes(tag.id) && 'portal-sidebar-tag--active'
                        )}
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

export default PublicSidebar;